// @ts-strict-ignore
/**
 * Global Variant Create View
 * 
 * Similar to ProductVariantCreate but WITHOUT product ID
 * Creates variants with globalVariant: true
 */

import { WindowTitle } from "@dashboard/components/WindowTitle";
import { DEFAULT_INITIAL_SEARCH_DATA } from "@dashboard/config";
import {
  useFileUploadMutation,
  useProductVariantChannelListingUpdateMutation,
  useUpdateMetadataMutation,
  useUpdatePrivateMetadataMutation,
} from "@dashboard/graphql";
import { useQuery, useMutation, gql } from "@apollo/client";
import useNavigator from "@dashboard/hooks/useNavigator";
import useNotifier from "@dashboard/hooks/useNotifier";
import useShop from "@dashboard/hooks/useShop";
import useAppChannel from "@dashboard/components/AppLayout/AppChannelContext";
import useCategorySearch from "@dashboard/searches/useCategorySearch";
import useCollectionSearch from "@dashboard/searches/useCollectionSearch";
import usePageSearch from "@dashboard/searches/usePageSearch";
import useProductSearch from "@dashboard/searches/useProductSearch";
import useWarehouseSearch from "@dashboard/searches/useWarehouseSearch";
import useAttributeValueSearchHandler from "@dashboard/utils/handlers/attributeValueSearchHandler";
import createMetadataCreateHandler from "@dashboard/utils/handlers/metadataCreateHandler";
import { mapEdgesToItems } from "@dashboard/utils/maps";
import { warehouseAddPath } from "@dashboard/warehouses/urls";
import React from "react";
import { useIntl } from "react-intl";
import { getMutationErrors, weight } from "../../misc";
import ProductVariantCreatePage from "../components/ProductVariantCreatePage";
import { ProductVariantCreateData } from "../components/ProductVariantCreatePage/form";
import { productVariantEditUrl } from "../urls";
import { variantCreateMessages as messages } from "./messages";
import {
  getAttributesAfterFileAttributesUpdate,
} from "@dashboard/attributes/utils/data";
import {
  handleUploadMultipleFiles,
  prepareAttributesInput,
} from "@dashboard/attributes/utils/handlers";
import { AttributeInput } from "@dashboard/components/Attributes";

export const GlobalVariantCreate = () => {
  const navigate = useNavigator();
  const notify = useNotifier();
  const shop = useShop();
  const intl = useIntl();
  const { availableChannels } = useAppChannel(false);

  // Reset any stuck loading states when component unmounts
  React.useEffect(() => {
    return () => {
      // Cleanup function runs when component unmounts
      console.log('GlobalVariantCreate unmounting - cleaning up');
    };
  }, []);

  const {
    loadMore: fetchMoreWarehouses,
    search: searchWarehouses,
    result: searchWarehousesResult,
  } = useWarehouseSearch({
    variables: {
      first: 100,
      channnelsId: [],
      query: "",
    },
    skip: true,
  });

  const [uploadFile, uploadFileOpts] = useFileUploadMutation({});

  // For global variants, we need to fetch all available product types to get their variant attributes grouped
  // since there's no product to get them from
  const { data: attributesData } = useQuery(gql`
    query GlobalVariantAttributes {
      productTypes(first: 100) {
        edges {
          node {
            id
            selectionVariantAttributes: variantAttributes(variantSelection: VARIANT_SELECTION) {
              id
              name
              slug
              inputType
              entityType
              valueRequired
              unit
              choices(first: 100) {
                edges {
                  node {
                    id
                    name
                    slug
                    value
                    plainText
                    richText
                    boolean
                    date
                    dateTime
                    reference
                    file {
                      url
                      contentType
                    }
                  }
                }
              }
            }
            nonSelectionVariantAttributes: variantAttributes(variantSelection: NOT_VARIANT_SELECTION) {
              id
              name
              slug
              inputType
              entityType
              valueRequired
              unit
              choices(first: 100) {
                edges {
                  node {
                    id
                    name
                    slug
                    value
                    plainText
                    richText
                    boolean
                    date
                    dateTime
                    reference
                    file {
                      url
                      contentType
                    }
                  }
                }
              }
            }
            variantAttributesGrouped(variantSelection: ALL) {
              parent {
                id
                name
                slug
              }
              attributes {
                attribute {
                  id
                  name
                  slug
                  inputType
                  entityType
                  valueRequired
                  unit
                  choices(first: 100) {
                    edges {
                      node {
                        id
                        name
                        slug
                        value
                        plainText
                        richText
                        boolean
                        date
                        dateTime
                        reference
                        file {
                          url
                          contentType
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  `);

  // Create a mock product with default channel for global variants pricing
  // and include all available variant attributes properly grouped
  const mockProductWithChannels = React.useMemo(() => {
    // Always use default channel for global variants
    const defaultChannel = availableChannels.find(ch => ch.slug === "default-channel") || availableChannels[0];

    if (!defaultChannel) return null;

    // Get the first product type (they all should have the same variant attributes)
    const firstProductType = attributesData?.productTypes?.edges?.[0]?.node;

    if (!firstProductType) {
      return {
        channelListings: [{
          __typename: "ProductChannelListing" as const,
          id: defaultChannel.id,
          isPublished: true,
          publishedAt: null,
          channel: {
            __typename: "Channel" as const,
            id: defaultChannel.id,
            name: defaultChannel.name,
            currencyCode: defaultChannel.currencyCode,
          },
        }],
        productType: {
          __typename: "ProductType" as const,
          id: "global-variant-type",
          name: "Global Variant",
          hasVariants: true,
          selectionVariantAttributes: [],
          nonSelectionVariantAttributes: [],
        },
      };
    }

    return {
      channelListings: [{
        __typename: "ProductChannelListing" as const,
        id: defaultChannel.id,
        isPublished: true,
        publishedAt: null,
        channel: {
          __typename: "Channel" as const,
          id: defaultChannel.id,
          name: defaultChannel.name,
          currencyCode: defaultChannel.currencyCode,
        },
      }],
      productType: {
        __typename: "ProductType" as const,
        id: firstProductType.id,
        name: "Global Variant",
        hasVariants: true,
        selectionVariantAttributes: firstProductType.selectionVariantAttributes || [],
        nonSelectionVariantAttributes: firstProductType.nonSelectionVariantAttributes || [],
        // Include the grouped variant attributes for proper parent/child separation
        variantAttributesGrouped: firstProductType.variantAttributesGrouped || [],
      },
    };
  }, [availableChannels, attributesData]);

  // Custom mutation for global variants that doesn't request the product field
  // This prevents crashes when product is null
  const [variantCreate, variantCreateResult] = useMutation(gql`
    mutation CreateGlobalVariant($input: ProductVariantCreateInput!) {
      productVariantCreate(input: $input) {
        productVariant {
          id
          name
          sku
          # NOTE: We don't request 'product' field here because it's null for global variants
          # and would cause frontend crashes when trying to access product.name, etc.
        }
        errors {
          field
          message
          code
        }
      }
    }
  `, {
    // Refetch the global variants list after creating a new one
    refetchQueries: ["GlobalProductVariants"],
    awaitRefetchQueries: true,
    onCompleted: data => {
      const variantId = data?.productVariantCreate?.productVariant?.id;

      if (!variantId) {
        notify({
          status: "error",
          text: intl.formatMessage(messages.variantCreatedError),
        });
        return;
      }

      notify({
        status: "success",
        text: "Global variant created successfully",
      });

      // Navigate to bulk edit page instead of variant edit
      navigate("/products/variants/bulk-edit-global-variants", {
        resetScroll: true,
      });
    },
    onError: error => {
      console.error('Error creating global variant:', error);
      notify({
        status: "error",
        text: error.message || "Failed to create global variant",
      });
    },
  });

  const [updateChannels] = useProductVariantChannelListingUpdateMutation({
    onError: error => {
      console.error('Error updating channel listings:', error);
      notify({
        status: "error",
        text: `Failed to update pricing: ${error.message}`,
      });
    },
    // Reset the mutation state to prevent it from affecting other pages
    fetchPolicy: "no-cache",
  });
  const [updateMetadata] = useUpdateMetadataMutation({});
  const [updatePrivateMetadata] = useUpdatePrivateMetadataMutation({});

  const handleCreate = async (formData: ProductVariantCreateData) => {
    const uploadFilesResult = await handleUploadMultipleFiles(
      formData.attributesWithNewFileValue,
      variables => uploadFile({ variables }),
    );

    const updatedFileAttributes = getAttributesAfterFileAttributesUpdate(
      formData.attributesWithNewFileValue,
      uploadFilesResult,
    );

    // For global variants, create with minimal data first
    // Then update channels separately to avoid product validation
    const variantCreateResult = await variantCreate({
      variables: {
        input: {
          attributes: prepareAttributesInput({
            attributes: formData.attributes.filter(
              attribute => attribute.value?.length && attribute.value[0] !== "",
            ),
            prevAttributes: null,
            updatedFileAttributes,
          }),
          // NO product field - backend made it optional for global variants
          globalVariant: true, // This marks it as a global variant
          sku: formData.sku,
          name: formData.variantName,
          // Skip stocks for global variants to avoid channel validation
          stocks: [],
          trackInventory: false,
          weight: weight(formData.weight),
          quantityLimitPerCustomer: Number(formData.quantityLimitPerCustomer) || null,
          preorder: formData.isPreorder
            ? {
              globalThreshold: formData.globalThreshold
                ? parseInt(formData.globalThreshold, 10)
                : null,
              endDate: formData.preorderEndDateTime || null,
            }
            : undefined,
        } as any, // Cast to any since TypeScript types haven't been regenerated yet
        firstValues: 10,
      },
    });

    const variantCreateResultErrors = getMutationErrors(variantCreateResult);

    if (variantCreateResultErrors.length > 0) {
      return { id: null, errors: variantCreateResultErrors };
    }

    const id = variantCreateResult.data?.productVariantCreate?.productVariant?.id;

    if (!id) {
      return { id: null, errors: [] };
    }

    // IMPORTANT: Update channel listings with pricing for global variants
    // Global variants need pricing just like regular variants
    console.log('Updating channel listings for global variant:', id);
    console.log('Channel listings data:', formData.channelListings);

    const updateChannelsResult = await updateChannels({
      variables: {
        id,
        input: formData.channelListings.map(listing => ({
          channelId: listing.id,
          costPrice: listing.value.costPrice || null,
          price: listing.value.price,
          preorderThreshold: listing.value.preorderThreshold,
        })),
      },
    });

    const updateChannelsErrors = getMutationErrors(updateChannelsResult);

    if (updateChannelsErrors.length > 0) {
      console.error('Error updating channel listings:', updateChannelsErrors);
    } else {
      console.log('Channel listings updated successfully');
    }

    return { id, errors: updateChannelsErrors };
  };

  const handleSubmit = createMetadataCreateHandler(
    handleCreate,
    updateMetadata,
    updatePrivateMetadata,
  );

  const handleVariantClick = (id: string) => navigate(productVariantEditUrl(id));

  const handleAssignAttributeReferenceClick = (attribute: AttributeInput) =>
    navigate(
      `/products/variants/add-variant?action=assign-attribute-value&id=${attribute.id}`,
    );

  const {
    loadMore: loadMorePages,
    search: searchPages,
    result: searchPagesOpts,
  } = usePageSearch({
    variables: DEFAULT_INITIAL_SEARCH_DATA,
  });

  const {
    loadMore: loadMoreProducts,
    search: searchProducts,
    result: searchProductsOpts,
  } = useProductSearch({
    variables: DEFAULT_INITIAL_SEARCH_DATA,
  });

  const {
    loadMore: loadMoreCategories,
    search: searchCategories,
    result: searchCategoriesOpts,
  } = useCategorySearch({
    variables: DEFAULT_INITIAL_SEARCH_DATA,
  });

  const {
    loadMore: loadMoreCollections,
    search: searchCollections,
    result: searchCollectionsOpts,
  } = useCollectionSearch({
    variables: DEFAULT_INITIAL_SEARCH_DATA,
  });

  const {
    loadMore: loadMoreAttributeValues,
    search: searchAttributeValues,
    result: searchAttributeValuesOpts,
    reset: searchAttributeReset,
  } = useAttributeValueSearchHandler(DEFAULT_INITIAL_SEARCH_DATA);

  const fetchMoreReferencePages = {
    hasMore: searchPagesOpts.data?.search?.pageInfo?.hasNextPage,
    loading: searchPagesOpts.loading,
    onFetchMore: loadMorePages,
  };

  const fetchMoreReferenceProducts = {
    hasMore: searchProductsOpts.data?.search?.pageInfo?.hasNextPage,
    loading: searchProductsOpts.loading,
    onFetchMore: loadMoreProducts,
  };

  const fetchMoreReferenceCategories = {
    hasMore: searchCategoriesOpts.data?.search?.pageInfo?.hasNextPage,
    loading: searchCategoriesOpts.loading,
    onFetchMore: loadMoreCategories,
  };

  const fetchMoreReferenceCollections = {
    hasMore: searchCollectionsOpts.data?.search?.pageInfo?.hasNextPage,
    loading: searchCollectionsOpts.loading,
    onFetchMore: loadMoreCollections,
  };

  const fetchMoreAttributeValues = {
    hasMore: !!searchAttributeValuesOpts.data?.attribute?.choices?.pageInfo?.hasNextPage,
    loading: !!searchAttributeValuesOpts.loading,
    onFetchMore: loadMoreAttributeValues,
  };

  const attributeValues = mapEdgesToItems(searchAttributeValuesOpts?.data?.attribute.choices) || [];

  const disableForm =
    uploadFileOpts.loading ||
    variantCreateResult.loading;

  return (
    <>
      <WindowTitle
        title="Create Global Variant"
      />
      <ProductVariantCreatePage
        productId="" // Empty - no product
        defaultVariantId={undefined}
        disabled={disableForm}
        searchWarehouses={searchWarehouses}
        errors={variantCreateResult.data?.productVariantCreate.errors || []}
        header="Create Global Variant"
        backHref="/products/variants/bulk-edit-global-variants"
        fetchMoreWarehouses={fetchMoreWarehouses}
        searchWarehousesResult={searchWarehousesResult}
        product={mockProductWithChannels as any} // Mock product with channels for pricing
        attributeValues={attributeValues}
        onSubmit={handleSubmit}
        onVariantClick={handleVariantClick}
        onWarehouseConfigure={() => navigate(warehouseAddPath)}
        onVariantReorder={() => { }} // No reorder for global variants
        saveButtonBarState={variantCreateResult.loading ? "loading" : "default"}
        weightUnit={shop?.defaultWeightUnit}
        assignReferencesAttributeId={undefined}
        onAssignReferencesClick={handleAssignAttributeReferenceClick}
        referencePages={mapEdgesToItems(searchPagesOpts?.data?.search) || []}
        referenceProducts={mapEdgesToItems(searchProductsOpts?.data?.search) || []}
        referenceCategories={mapEdgesToItems(searchCategoriesOpts?.data?.search) || []}
        referenceCollections={mapEdgesToItems(searchCollectionsOpts?.data?.search) || []}
        fetchReferencePages={searchPages}
        fetchMoreReferencePages={fetchMoreReferencePages}
        fetchReferenceProducts={searchProducts}
        fetchMoreReferenceProducts={fetchMoreReferenceProducts}
        fetchReferenceCategories={searchCategories}
        fetchMoreReferenceCategories={fetchMoreReferenceCategories}
        fetchReferenceCollections={searchCollections}
        fetchMoreReferenceCollections={fetchMoreReferenceCollections}
        fetchAttributeValues={searchAttributeValues}
        fetchMoreAttributeValues={fetchMoreAttributeValues}
        onCloseDialog={() => navigate("/products/variants/add-variant")}
        onAttributeSelectBlur={searchAttributeReset}
      />
    </>
  );
};

export default GlobalVariantCreate;
